<template>
  <div class="grid grid-cols-2 gap-4">
    <div>
      <LabeledText v-if="booking" :label="t('relatedBooking')" class="mb-4">
        <p>{{ `${t('bookingNumber')} ${booking?.bookingNumber}` }}</p>
        <p>
          {{ booking?.customerData?.firstName ?? 'n/a' }}
          {{ booking?.customerData?.lastName ?? 'n/a' }}
        </p>
        <p>
          {{ booking?.bookedCar.vehicleType.make.name }}
          {{ booking?.bookedCar.vehicleType.model }}
        </p>
      </LabeledText>
      <CVButton v-if="!showInput" outline @click="showInput = true">{{
        t('switchBooking')
      }}</CVButton>
      <div v-else class="flex items-center justify-start gap-4">
        <SearchBookingInput
          class="w-full"
          :label="inputLabel"
          :error="errorMessage"
          @update-booking="handleUpdateBooking"
        />
        <CVButton v-if="booking" variant="warning" @click="showInput = false">{{
          t('cancel')
        }}</CVButton>
      </div>
    </div>
    <div v-if="booking && counterpartyOptions.length">
      <SelectField
        name="counterpartyType"
        :label="t('counterpartyType.label')"
        :options="counterpartyOptions"
      />
      <LabeledText class="mt-4">
        <template v-if="counterpartyType === CounterpartyType.AGENCY && booking?.agency">
          <p>{{ booking?.agency?.company ?? 'n/a' }}</p>
          <p>{{ booking?.agency?.firstName ?? 'n/a' }} {{ booking?.agency?.lastName ?? 'n/a' }}</p>
        </template>
        <template v-else>
          <p>
            {{ booking?.customerData?.firstName ?? 'n/a' }}
            {{ booking?.customerData?.lastName ?? 'n/a' }}
          </p>
        </template>
      </LabeledText>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import SearchBookingInput from '@/components/booking-forms/SearchBookingInput.vue';
import SelectField from '@/components/SelectField.vue';
import { CounterpartyType } from '@/entities/ledger/ledger-entry.entity';
import type { ManyBookingsBooking } from '@/entities/bookings/many-bookings-booking.entity';
import { useI18n } from 'vue-i18n';
import { useField } from 'vee-validate';
import { computed, ref, watch } from 'vue';
import { useCounterpartyTypeOptions } from '@/hooks/use-counterparty-type-options';

const { t } = useI18n();

const booking = ref<ManyBookingsBooking>();
const showInput = ref(true);

const inputLabel = computed(() => (!booking.value ? t('linkBooking') : undefined));

const { value: bookingId, errorMessage } = useField<string | undefined>('bookingId');
const { value: counterpartyType, setValue: setCounterpartyType } =
  useField<CounterpartyType>('counterpartyType');
const { setValue: setCustomerId } = useField<string | undefined>('customerId');

const counterpartyOptions = useCounterpartyTypeOptions(
  computed(() => [
    CounterpartyType.CUSTOMER,
    ...(booking.value?.agency ? [CounterpartyType.AGENCY] : []),
  ]),
);

watch(counterpartyType, () => {
  if (counterpartyType.value === CounterpartyType.AGENCY && booking.value?.agency) {
    setCustomerId(booking.value.agency.id);
  } else if (counterpartyType.value === CounterpartyType.CUSTOMER) {
    setCustomerId(undefined);
  }
});

const handleUpdateBooking = (selectedBooking: ManyBookingsBooking | undefined) => {
  booking.value = selectedBooking ?? booking.value;
  bookingId.value = selectedBooking?.id ?? bookingId.value;
  setCounterpartyType(CounterpartyType.CUSTOMER);
  if (selectedBooking) {
    showInput.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "relatedBooking": "Related booking",
    "bookingNumber": "Bnr.",
    "switchBooking": "Switch booking",
    "linkBooking": "Link Booking"
  },
  "de": {
    "relatedBooking": "Verknüpfte Buchung",
    "bookingNumber": "Bnr.",
    "switchBooking": "Buchung wechseln",
    "linkBooking": "Buchung verknüpfen"
  },
  "es": {
    "relatedBooking": "Reserva vinculada",
    "bookingNumber": "N.º res.",
    "switchBooking": "Cambiar reserva",
    "linkBooking": "Vincular reserva"
  }
}
</i18n>
